<script lang="ts">
  /**
   * Phase R3 — bottom sheet de snooze (factice v1).
   *
   * Affiché en overlay au-dessus de l'app quand `snoozeState.threadId`
   * est non-null. Tap sur un preset → écrit en IDB via setSnooze + close.
   * Le filtre "En attente" lit ce timestamp pour cacher la row.
   */

  import { setSnooze } from "../gmail/threadFlags.svelte";
  import { showToast } from "../snackbar/store.svelte";
  import { snoozeState, closeSnooze } from "./snoozeStore.svelte";
  import { haptic } from "../gestures/haptic";
  import { dismissSheet } from "../gestures/dismissSheet";
  import { lockBodyScroll, unlockBodyScroll } from "../gestures/bodyLock";

  $effect(() => {
    if (snoozeState.threadId) {
      lockBodyScroll();
      return () => unlockBodyScroll();
    }
  });

  // Calcule les timestamps des presets relativement à maintenant.
  function in3h(): number  { return Date.now() + 3 * 3600_000; }
  function tonight(): number {
    const d = new Date();
    d.setHours(18, 0, 0, 0);
    if (d.getTime() <= Date.now()) d.setDate(d.getDate() + 1);
    return d.getTime();
  }
  function tomorrow(): number {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d.getTime();
  }
  function thisWeekend(): number {
    const d = new Date();
    const dow = d.getDay(); // 0=dim, 6=sam
    const daysToSat = (6 - dow + 7) % 7 || 7;
    d.setDate(d.getDate() + daysToSat);
    d.setHours(9, 0, 0, 0);
    return d.getTime();
  }
  function nextWeek(): number {
    const d = new Date();
    const dow = d.getDay();
    const daysToMon = ((1 - dow + 7) % 7) || 7;
    d.setDate(d.getDate() + daysToMon);
    d.setHours(9, 0, 0, 0);
    return d.getTime();
  }

  interface Preset {
    id: string;
    label: string;
    meta: string;
    when: () => number;
  }

  const presets: Preset[] = [
    { id: "3h",      label: "Dans 3 heures",     meta: "cet après-midi",  when: in3h },
    { id: "tonight", label: "Ce soir",           meta: "18:00",           when: tonight },
    { id: "tmrw",    label: "Demain matin",      meta: "09:00",           when: tomorrow },
    { id: "we",      label: "Ce week-end",       meta: "samedi 09:00",    when: thisWeekend },
    { id: "nextwk",  label: "Semaine prochaine", meta: "lundi 09:00",     when: nextWeek },
  ];

  async function pick(p: Preset) {
    const id = snoozeState.threadId;
    if (!id) return;
    haptic("medium");
    await setSnooze(id, p.when());
    showToast({ message: `Reporté · ${p.label.toLowerCase()}` });
    closeSnooze();
  }
</script>

{#if snoozeState.threadId}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="overlay" onclick={closeSnooze} role="presentation">
    <div
      class="sheet"
      onclick={(e) => e.stopPropagation()}
      role="dialog"
      aria-label="Reporter le mail"
      aria-modal="true"
      tabindex="-1"
      use:dismissSheet={{ onClose: closeSnooze }}
    >
      <div class="grabber" aria-hidden="true" data-dismiss-handle></div>
      <div class="title-row" data-dismiss-handle>
        <h2>Reporter</h2>
        <span class="kicker">Snooze</span>
      </div>
      {#each presets as p (p.id)}
        <button class="preset" onclick={() => pick(p)}>
          <svg class="icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="13" r="8"/>
            <path d="M12 9v4l2.5 2.5"/>
            <path d="M5 4l3-2M19 4l-3-2"/>
          </svg>
          <span class="label">{p.label}</span>
          <span class="meta">{p.meta}</span>
        </button>
      {/each}
    </div>
  </div>
{/if}

<style>
  .overlay {
    position: fixed;
    inset: 0;
    z-index: 250;
    background: rgba(0, 0, 0, 0.35);
    display: flex;
    align-items: flex-end;
    animation: fade 180ms ease;
  }
  @keyframes fade {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  .sheet {
    width: 100%;
    background: var(--bg);
    color: var(--fg);
    font-family: var(--font-sans);
    border-top-left-radius: 22px;
    border-top-right-radius: 22px;
    padding: 14px 0 calc(env(safe-area-inset-bottom) + 24px);
    box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.18);
    animation: slideup 220ms cubic-bezier(0.32, 0.72, 0, 1);
  }
  @keyframes slideup {
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
  }

  .grabber {
    width: 36px;
    height: 4px;
    border-radius: 2px;
    background: var(--border);
    margin: 0 auto 8px;
  }
  .title-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding: 8px 24px 12px;
  }
  .title-row h2 {
    font-family: var(--font-serif);
    font-size: 26px;
    font-style: italic;
    font-weight: 400;
    color: var(--fg);
    margin: 0;
  }
  .kicker {
    font-family: var(--font-mono);
    font-size: 10px;
    color: var(--fg-subtle);
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .preset {
    width: 100%;
    background: transparent;
    border: none;
    border-top: 0.5px solid var(--border-subtle);
    padding: 14px 24px;
    display: flex;
    align-items: center;
    gap: 14px;
    color: var(--fg);
    cursor: pointer;
    text-align: left;
    -webkit-tap-highlight-color: transparent;
  }
  .preset:active {
    background: color-mix(in srgb, var(--fg) 4%, transparent);
  }
  .preset:last-child { border-bottom: 0.5px solid var(--border-subtle); }

  .icon { color: var(--fg-muted); flex-shrink: 0; }
  .label {
    flex: 1;
    font-family: var(--font-sans);
    font-size: 15px;
    font-weight: 500;
    letter-spacing: -0.01em;
  }
  .meta {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--fg-subtle);
    letter-spacing: 0.02em;
  }
</style>
